import { useMemo, useState } from 'react';
import { Button } from '@components/ui';
import { Sparkles, MoveRight, Plus, RefreshCw } from 'lucide-react';
import { useSummarizer } from '@core/store/useSummarizer';
import { PromptLibraryItem } from '../../../../config/prompt-library';

export const PromptLibrary: React.FC = () => {
  const { 
    prompts, 
    categories,
    isLoadingPromptLibrary,
    promptLibraryError,
    setActivePrompt, 
    setShowCustomPromptModal,
    fetchPromptLibrary,
    clearPromptLibraryCache
  } = useSummarizer();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPrompts = useMemo(() => {
    let filtered = [...prompts];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (prompt) =>
          prompt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (prompt.description && prompt.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // show custom prompts first
    return filtered.sort((a, b) => {
      if (a.category === 'custom' && b.category !== 'custom') {
        return -1;
      }
      if (a.category !== 'custom' && b.category === 'custom') {
        return 1;
      }
      return 0;
    });
  }, [selectedCategory, searchQuery, prompts]);

  const handlePromptClick = (prompt: PromptLibraryItem) => {
    setActivePrompt(prompt);
  };

  const handleRefresh = async () => {
    clearPromptLibraryCache();
    await fetchPromptLibrary();
  };

  const promptCategories = [
    { id: 'all', name: 'All', icon: '🌍' },
    ...categories.map(category => ({ 
      id: category, 
      name: category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' '), 
      icon: '📁' 
    })),
    { id: 'custom', name: 'Custom', icon: '✨' },
  ];

  // Show loading state
  if (isLoadingPromptLibrary) {
    return (
      <div className="w-full bg-white overflow-y-auto flex flex-col">
        <div className="p-4 border-b">
          <p className="font-medium mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            Prompt Library
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading prompt library...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (promptLibraryError) {
    return (
      <div className="w-full bg-white overflow-y-auto flex flex-col">
        <div className="p-4 border-b">
          <p className="font-medium mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            Prompt Library
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">Failed to load prompt library</p>
            <p className="text-gray-600 text-sm mb-4">{promptLibraryError}</p>
            <Button onClick={() => fetchPromptLibrary()} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white overflow-y-auto flex flex-col">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between w-full mb-2">
          <p className="font-medium mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            Prompt Library
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoadingPromptLibrary}
              className="p-2"
              title="Refresh prompts"
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingPromptLibrary ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              className="text-sm bg-black text-white hover:bg-black hover:text-white"
              onClick={() => setShowCustomPromptModal(true)}
            >
              <Plus />
              <span className="hidden sm:!inline">Create Custom Prompt</span>
              <span className="sm:!hidden">Custom Prompt</span>
            </Button>
          </div>
        </div>

        <div className="relative mb-3">
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-3 pr-4 py-2 border rounded-lg text-sm focus:outline-none"
          />
        </div>

        <div className="flex gap-1 overflow-x-auto p-2">
          {promptCategories.map((category) => (
            <Button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              variant={selectedCategory === category.id ? 'default' : 'outline'}
              size="sm"
            >
              {category.name}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredPrompts.map((prompt) => (
          <div
            key={prompt.id}
            onClick={() => handlePromptClick(prompt)}
            className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{prompt.icon}</span>
              <div className="flex-1">
                <p className="font-medium text-sm mb-1 group-hover:text-blue-600">
                  {prompt.title}
                </p>
                <p className="text-xs text-gray-600 line-clamp-2">
                  {prompt.description || 
                   (prompt.prompt && prompt.prompt.length > 0 
                     ? prompt.prompt
                         .replace(/\{content\}/g, '')
                         .replace(/\*\*/g, '')
                         .replace(/\n+/g, ' ')
                         .trim()
                         .substring(0, 100) + (prompt.prompt.length > 100 ? '...' : '')
                     : 'No description available')
                  }
                </p>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity align-middle">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                  <MoveRight />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
